// "New track". Audio comes first and biggest — recording and loops are what
// most seeds are made of: record into a new audio track, or open your clips
// on it. Then the instruments (a MIDI lane with an empty clip, opened in the
// piano roll). A dialog on a wide screen, a sheet up from the bottom on a
// phone. Presentation only — RoomPage turns a pick into intents.

import { Library, Mic } from "lucide-react";
import type { InstrumentId } from "@shared/instruments.js";
import { InstrumentPicker } from "@web-shared/components/InstrumentPicker";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@web-shared/components/ui/dialog";
import { cn } from "@web-shared/lib/utils";

export function AddTrackSheet({
  open,
  onOpenChange,
  narrow,
  onInstrument,
  onRecord,
  onLoops,
  onPreview,
  previewing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  narrow: boolean;
  onInstrument: (id: InstrumentId) => void;
  /** A new audio track, ready to record into. */
  onRecord: () => void;
  /** A new audio track, with your clip library open beside it. */
  onLoops: () => void;
  onPreview: (id: InstrumentId) => void;
  previewing?: InstrumentId | null;
}) {
  const pick = (act: () => void) => () => {
    act();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[90dvh] overflow-y-auto border-panelline bg-sunk",
          narrow &&
            "sheet-up bottom-0 left-0 top-auto max-w-none translate-x-0 translate-y-0 rounded-b-none rounded-t-3xl pb-[calc(1.25rem+env(safe-area-inset-bottom))]",
          !narrow && "max-w-xl",
        )}
      >
        <DialogHeader>
          <DialogTitle>New track</DialogTitle>
          <DialogDescription>Record something, bring in loops, or play an instrument.</DialogDescription>
        </DialogHeader>

        <section
          aria-label="Audio"
          className="rounded-2xl border border-primary/40 bg-[color-mix(in_srgb,var(--primary)_8%,transparent)] p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Mic className="size-6" />
            </span>
            <div className="min-w-0">
              <p className="text-base font-semibold">Audio</p>
              <p className="text-xs text-muted-foreground">
                Vocals, guitar, a beat you tapped on the table — or loops and one-shots from your clips.
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={pick(onRecord)}
              className="flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-white"
            >
              <span className="size-2.5 rounded-full bg-[var(--removed)]" aria-hidden />
              Record
            </button>
            <button
              type="button"
              onClick={pick(onLoops)}
              className="flex items-center justify-center gap-2 rounded-xl border border-panelline bg-panel px-3 py-2.5 text-sm font-semibold hover:border-foreground/30"
            >
              <Library className="size-4" />
              Add loops
            </button>
          </div>
        </section>

        <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Instruments</p>
        <InstrumentPicker onPick={(id) => pick(() => onInstrument(id))()} onPreview={onPreview} previewing={previewing} />
      </DialogContent>
    </Dialog>
  );
}
